import { Injectable } from '@angular/core';
import { Subscription, Subject, of, timer } from 'rxjs';
import { ConfirmAction, ConfirmDialogData } from '../dialogs/confirm/confirm.dialog';
import { Store } from '@ngxs/store';
import { AuthState } from '@app/modules/core/state/auth.state';
import { Logout } from '@app/modules/core/actions/auth.action';
import { timeoutWith } from 'rxjs/operators';
import { DialogService } from '@app/modules/core/services/dialog.service';

class TimePeriod {
  /**
   * Gets the time period as milliseconds.
   */
  get asMilliseconds(): number {
    return (
      ((this.hours * 60 + this.minutes) * 60 + this.seconds) * 1000 +
      this.milliseconds
    );
  }

  private readonly hours: number;
  private readonly minutes: number;
  private readonly seconds: number;
  private readonly milliseconds: number;

  constructor(config: {
    hours?: number;
    minutes?: number;
    seconds?: number;
    milliseconds?: number;
  }) {
    this.hours = config.hours || 0;
    this.minutes = config.minutes || 0;
    this.seconds = config.seconds || 0;
    this.milliseconds = config.milliseconds || 0;
  }
}

@Injectable({
  providedIn: 'root'
})
export class IdleMonitorService {
  private idleSubscription: Subscription;

  /**
   * The length of time that the user has to be 'idle' for before a dialog will show.
   */
  private readonly idlePeriod = new TimePeriod({ minutes: 30 });


  /**
   * The length of time the user has to respond to the dialog.
   * Once the time passes then the dialog will be closed and the user logged out.
   */
  private readonly confirmPeriod = new TimePeriod({ seconds: 30 });

  /**
   * Subject that contains the action of the dialog.
   */
  private readonly dialogAction$ = new Subject<ConfirmAction>();

  constructor(private dialogService: DialogService, private store: Store) {
    // Subscribe to the outcome of a dialog being closed.
    this.dialogAction$.subscribe({
      next: result => {
        if (result === 'cancel') {
          // Added parameter 'payload' to Logout constructor
          const currentAccessToken = this.store.selectSnapshot( AuthState.token );
          this.store.dispatch(new Logout( currentAccessToken ));
          this.dialogService.closeAll();
          return;
        }
        this.startMonitoring();
      },
    });
  }

  private openIdleDialog() {
    console.log('[IdleMonitor] - Opening Dialog');

    const dialogData: ConfirmDialogData = {
      title: 'Hello, are you still there?',
      message: 'Do you want to continue?',
      /* TODO confirmType: 'info', NOT IN HUMM VERSION*/
      okLabel: 'Yes, continue',
      cancelLabel: 'No, log me out',
    };

    const cancel: ConfirmAction = 'cancel';

     // Show a dialog and time it out after the given `confirmPeriod`.
    // This will emit a `cancel` action if the user doesn't take action.
    this.dialogService
      .showConfirm(dialogData)
      .pipe(timeoutWith(this.confirmPeriod.asMilliseconds, of(cancel)))
      .subscribe(val => this.dialogAction$.next(val));
  }

  /**
   * Stop timing.
   */
  public stopMonitoring() {
    if (this.idleSubscription) {
      console.log('[IdleMonitor] - Stop Monitoring');
      this.idleSubscription.unsubscribe();
    }
  }

  /**
   * Start timing. This should be called every time the user performs
   * an action that indicates they are active.
   *
   * *Note: If we're already timing then this will restart the timer.*
   */
  public startMonitoring() {
    this.stopMonitoring();

    if (!this.store.selectSnapshot(AuthState.loggedIn)) {
      return;
    }

    console.log('[IdleMonitor] - Start Monitoring');
    console.log('this.idlePeriod.asMilliseconds' + this.idlePeriod.asMilliseconds);

    this.idleSubscription = timer(this.idlePeriod.asMilliseconds).subscribe(
      () => this.openIdleDialog()
    );
  }

}
